import { Box, Divider, styled, Typography } from '@mui/material';
import { Row } from 'components/Layout';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { numberToCurrencyString } from 'further-ui/utils';
import { CurrencyTransactionOperation } from './types';

const Container = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.grey[300]}`,
  padding: theme.spacing(theme.spacingSizes.sm, theme.spacingSizes.lg),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(theme.spacingSizes.xs),
  width: '100%',
  backgroundColor: theme.palette.text.white,
}));

const Boundary = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(theme.spacingSizes.xs),
  width: '100%',
  maxWidth: '400px',
}));

const Label = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  width: '60%',
}));

const Value = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  width: '40%',
  textAlign: 'left',
}));

type Props = {
  income: number;
  costs: number;
  total: number;
  bankTransactionAmount?: number;
  currency: CurrencyCode;
  isTransfer: boolean;
  operation: CurrencyTransactionOperation;
  className?: string;
};

const CurrencyTransactionSummary: React.FC<Props> = ({
  className,
  operation,
  currency,
  isTransfer,
  income,
  costs,
  total,
  bankTransactionAmount,
}) => {
  const isStatementCurrency = !!bankTransactionAmount;
  const isCurrencySale = operation === CurrencyTransactionOperation.Sale;

  const transactionOperationName = isTransfer ? 'Transfer' : 'Payment';

  const headingText = isStatementCurrency
    ? 'Statement currency'
    : `${transactionOperationName} currency`;

  const values = isStatementCurrency
    ? [
        {
          label: 'Bank statement amount',
          value: bankTransactionAmount,
        },
        {
          label: isCurrencySale
            ? `Plus ${currency} incoming`
            : `Plus ${currency} ${isTransfer ? 'costs' : 'expenses'}`,
          value: isCurrencySale ? income : costs,
        },
        ...(!isTransfer
          ? [
              {
                label: isCurrencySale
                  ? `Minus ${currency} outgoing`
                  : `Minus ${currency} income`,
                value: isCurrencySale ? costs : income,
              },
            ]
          : []),
      ]
    : [
        {
          label: isCurrencySale ? `${currency} income` : `${currency} expenses`,
          value: isCurrencySale ? income : costs,
        },
        {
          label: isCurrencySale
            ? isTransfer
              ? `Minus ${currency} costs`
              : `${currency} outgoing`
            : `${currency} income`,
          value: isCurrencySale ? costs : income,
        },
      ];

  const summaryText = isStatementCurrency
    ? `Net ${transactionOperationName.toLowerCase()} amount ${currency}`
    : `Net ${currency} amount ${isCurrencySale ? 'incoming' : 'outgoing'}`;

  return (
    <Container className={className}>
      <Typography variant="body2" color="textPrimary" fontWeight={700}>
        {headingText}
      </Typography>
      <Boundary>
        {values.map((value) => (
          <Row
            centered
            fullWidth
            justify="space-between"
            spacing="sm"
            key={value.label}
          >
            <Label variant="body2">{value.label}</Label>
            <Value variant="body2">
              {numberToCurrencyString(value.value, {
                currency,
              })}
            </Value>
          </Row>
        ))}
        <Divider />
        <Row centered fullWidth justify="space-between" spacing="sm">
          <Label variant="body2" fontWeight="bold">
            {summaryText}
          </Label>
          <Value variant="body2" fontWeight="bold">
            {numberToCurrencyString(total, {
              currency,
            })}
          </Value>
        </Row>
      </Boundary>
    </Container>
  );
};

export default CurrencyTransactionSummary;
